import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { ArrowUpCircle, ArrowDownCircle, CircleDot } from "lucide-react"
import { cn } from "@/lib/utils"
import React from "react"

interface ImpactScoreProps {
  type: 'positive' | 'negative' | 'neutral'
  score: number | string
  label: string
}

const ImpactScore = ({ type, score, label }: ImpactScoreProps) => {
  const Icon = {
    positive: ArrowUpCircle,
    negative: ArrowDownCircle,
    neutral: CircleDot
  }[type]

  const styles = {
    positive: "bg-green-100 text-green-800",
    negative: "bg-red-100 text-red-800",
    neutral: "bg-gray-100 text-gray-800"
  }[type]

  return (
    <div className="flex items-center gap-2">
      <Badge variant="secondary" className={cn(styles)}>
        <Icon className="h-4 w-4 mr-1" />
        {score}
      </Badge>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  )
}

interface ImpactCardProps {
  title: string
  score1: {
    type: 'positive' | 'negative' | 'neutral'
    score: number | string
  }
  score1title: string
  score2?: {
    type: 'positive' | 'negative' | 'neutral'
    score: number | string
  }
  score2title?: string
  details_title?: string
  details?: string
  icon?: React.ComponentType<any>
  className?: string
}

export function ImpactCard({
  title,
  score1,
  score1title,
  score2,
  score2title,
  details_title,
  details,
  icon: Icon,
  className
}: ImpactCardProps) {
  return (
    <Card className={cn("shadow-sm", className)}>
      <CardHeader className="pb-1 p-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5" />}
          <CardTitle className="text-xl">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 p-2 pt-0">
        <div className="flex flex-col gap-2">
          <ImpactScore
            type={score1.type}
            score={score1.score}
            label={score1title}
          />
          {score2 && score2title && (
            <ImpactScore
              type={score2.type}
              score={score2.score}
              label={score2title}
            />
          )}
        </div>
        {details && details_title && (
          <>
            <Separator />
            <div className="text-sm">
              <h4 className="font-semibold mb-1">{details_title}:</h4>
              <p className="text-muted-foreground">{details}</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}